<template>
  <div
    class="i-payment-card"
    :class="{ highlighted: props.isHighlighted }"
  >
    <div
      v-if="props.isHighlighted"
      class="recommended"
    >
      Recommended
    </div>

    <div class="gradient">
      <div class="inner">
        <div class="top">
          <div class="details">
            <div class="title">{{ props.title }}</div>
            <!-- <div class="subtitle">{{ props.subtitle }}</div> -->
            <div class="description">{{ props.description }}</div>
            <div class="price">
              ${{ props.currentPriceWithCents }}<span class="currency">USD</span>
              <div
                v-if="props.reportStatus === ReportStatus.PREMIUM"
                class="previous-price"
              >
                <span class="amount">$179.90</span>
                <div class="discount">$50 Off Today</div>
              </div>
            </div>
            <div class="action">
              <iBaseButton
                v-bind="buttonStyle"
                @click="firePaymentAction"
              />
              <div
                v-if="props.reportStatus === ReportStatus.PREMIUM"
                class="coaching"
              >
                + Free 30-min coaching
              </div>
            </div>
          </div>
          <div
            class="perks"
            :style="{ paddingBottom: '0px' }"
          >
            <div
              v-for="(benefit, index) in props.benefitsPreview"
              :key="index"
              class="detail"
              :class="{ missing: !benefit.included }"
              v-html="benefit.title"
            ></div>
            <div
              v-if="!!props.benefits.length"
              class="more"
              :style="{ marginTop: '15px' }"
              @click="data.showCompleteBenefits = true"
            >
              Show complete list of features<img :src="iconChevronDown" />
            </div>
          </div>
          <div
            v-if="!!props.benefits.length && (isSmOrLarger || data.showCompleteBenefits)"
            class="perks"
          >
            <div
              v-if="!isSmOrLarger"
              class="more"
              @click="data.showCompleteBenefits = false"
            >
              Show less features<img :src="iconChevronUp" />
            </div>
            <div
              v-for="(benefit, index) in props.benefits"
              :key="index"
              class="detail"
              :class="{ missing: !benefit.included }"
            >
              {{ benefit.title }}
            </div>
          </div>
        </div>
        <div
          v-if="!!props.benefits.length && (isSmOrLarger || data.showCompleteBenefits)"
          class="pro-perks"
        >
          <div
            class="detail bold"
            :class="{ missing: props.reportStatus != ReportStatus.PREMIUM }"
          >
            30-minute Session With Expert Coach
          </div>
          <div class="list">
            <div
              class="detail"
              :class="{ missing: props.reportStatus != ReportStatus.PREMIUM }"
            >
              Breakdown Of Your Best-Fit Careers
            </div>
            <div
              class="detail"
              :class="{ missing: props.reportStatus != ReportStatus.PREMIUM }"
            >
              Clarity on How to Position Your Experience
            </div>
            <div
              class="detail"
              :class="{ missing: props.reportStatus != ReportStatus.PREMIUM }"
            >
              Role Targeting Guidance and Resume Tips
            </div>
            <div
              class="detail"
              :class="{ missing: props.reportStatus != ReportStatus.PREMIUM }"
            >
              Actionable Next Steps For Your Career Path
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, reactive } from "vue"
import { bluePurpleGradient, white } from "@/styles/colors"
import { breakpointSm } from "@/styles/variables"

import useIsScreenSize from "@/composables/useIsScreenSize"

// ASSETS
import iconChevronUp from "@/assets/icons/icon-chevron-up.svg"
import iconChevronDown from "@/assets/icons/icon-chevron-down.svg"

// TYPES
import ReportStatus from "@/shared/enums/ReportStatus"

// COMPONENTS
import iBaseButton from "@/components/base/BaseButton.vue"

// EMITS
const emit = defineEmits(["clicked", "payment-action-error"])

// PROPS
const props = defineProps({
  benefits: {
    type: Array as () => { included: boolean; title: string }[],
    default: () => []
  },
  benefitsPreview: {
    type: Array as () => { included: boolean; title: string }[],
    default: () => []
  },
  buttonText: {
    type: String,
    default: "GET MY RESULT"
  },
  currentPriceWithCents: {
    type: String
  },
  isButtonDisabled: {
    type: Boolean,
    default: false
  },
  isHighlighted: {
    type: Boolean,
    default: false
  },
  paymentAction: {
    type: Function,
    default: () => {
      console.log("Button clicked...")
    }
  },
  subtitle: {
    type: String,
    default: "Card Subtitle"
  },
  title: {
    type: String,
    default: "Card Title"
  },
  description: {
    type: String,
    default: "Card Description"
  },
  reportStatus: {
    type: String as () => ReportStatus,
    required: true
  }
})

// COMPOSABLES
const isSmOrLarger = useIsScreenSize(breakpointSm)

// DATA
const data = reactive({
  showCompleteBenefits: false
})

// COMPUTED
const buttonStyle = computed(() => {
  return {
    backgroundImage: bluePurpleGradient,
    borderRadius: "8px",
    fontWeight: "500",
    textColor: white,
    width: "100%",
    padding: isSmOrLarger.value ? "8px 70px" : "10px 6px",
    size: "medium",
    text: "Get My Results"
  }
})

// FUNCTIONS
const firePaymentAction = async () => {
  if (props.isButtonDisabled) return
  emit("clicked")
  try {
    await props.paymentAction()
  } catch {
    emit("payment-action-error")
  }
}
</script>

<style scoped lang="scss">
.i-payment-card {
  @include border-radius-md;

  position: relative;
  width: 100%;
  max-width: 370px;

  .recommended {
    @include border-radius-xxl;

    width: fit-content;
    background-image: $blue-purple-gradient;
    padding: 3px 12px;
    color: $white;
    position: absolute;
    left: 50%;
    transform: translate(-50%, -50%);
  }

  .gradient {
    @include border-radius-md;

    padding: 2px;
    height: 100%;

    .inner {
      @include flex(column, space-between, stretch);
      @include border-radius-md;

      height: 100%;
      padding: 30px;
      background-color: $white;

      .detail {
        @include flex(row, start, start);
        @include text-sm;

        font-weight: 400;
        color: $black950;
        list-style: none;

        &::before {
          content: "";
          display: inline-block;
          height: 20px;
          width: 23px;
          flex: 0 0 23px;
          background-image: url("@/assets/icons/icon-green-check.svg");
          background-repeat: no-repeat;
        }

        &.missing::before {
          background-image: url("@/assets/icons/icon-red-close.svg");
        }
      }

      .top {
        @include flex(column, start, stretch);

        .details {
          @include flex(column, start, stretch, $gap: 8px);
          @include text-sm;

          padding-bottom: 20px;
          border-bottom: 1px solid $zumthor100;

          .title {
            @include display-sm;

            margin-bottom: 5px;
          }

          .subtitle {
            @include text-xl;

            font-weight: 700;
          }

          .description {
            @media screen and (min-width: $breakpoint-md) {
              // min-height: 60px;
            }
          }

          .price {
            @include flex(row, start, end, $gap: 6px);
            @include display-sm;

            min-height: 60px;
            margin-bottom: 5px;

            .currency {
              @include text-sm;

              color: $comet500;
              margin-bottom: 4px;
            }

            .previous-price {
              text-align: right;
              color: #7d838a;
              margin-left: 10px;
              margin-bottom: 4px;

              .amount {
                @include text-md;

                font-weight: 600;
                position: relative;
              }

              .amount::before {
                position: absolute;
                content: "";
                left: 0;
                top: 50%;
                right: 0;
                border-top: 1px solid;
                border-color: inherit;
                transform: rotate(-10deg);
              }

              .discount {
                color: #353d49;

                // margin-right: 2px;

                font-weight: 600;

                @include text-xs;
              }
            }
          }

          .action {
            @include flex(column, start, center, $gap: 12px);

            min-height: 60px;

            @media screen and (min-width: $breakpoint-xl) {
              min-height: 80px;
            }

            .coaching {
              background-image: $blue-purple-gradient;
              color: transparent;
              background-clip: text;
              font-weight: 700;
            }
          }
        }

        .perks {
          @include flex(column, start, stretch, $gap: 14px);

          padding: 24px 0;

          .more {
            @include flex(row, center, center, wrap, 4px);
            @include text-sm;

            font-weight: 700;
            cursor: pointer;

            img {
              width: 15px;
              height: 15px;
            }
          }
        }
      }

      .pro-perks {
        @include flex(column, start, stretch, $gap: 14px);

        padding-top: 24px;
        border-top: 1px solid $zumthor100;

        .bold {
          font-weight: 700;
        }

        .list {
          @include flex(column, start, start, $gap: 14px);

          margin-left: 20px;
        }
      }
    }
  }

  &.highlighted {
    .gradient {
      background-image: $blue-purple-gradient;
      box-shadow: 0 0 0 8px #a162ff26;
    }
  }
}
</style>
